import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, Plus, Wand2 } from '@/lib/icons';
import { useEntity, useRefsOf } from '@/stores/selectors';
import { useCodexStore } from '@/stores/codexStore';
import { fieldsFor } from '@/lib/schemas/entityFields';
import { ENTITY_META, entityColor, entitySummary } from '@/config/entityMeta';
import { CanonBadge } from './CanonBadge';
import { EditableText } from './EditableText';
import { FieldRow } from './FieldRow';
import { RelationEditor } from './RelationEditor';
import { TagInput } from './TagInput';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CANON_STATUSES, CONFIDENCES, type FieldDef } from '@/types/entity';
import { newId } from '@/lib/id';
import { useFocus } from '@/lib/useFocus';

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
      {children}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

export function Inspector({ id }: { id: string }) {
  const navigate = useNavigate();
  const entity = useEntity(id);
  const refs = useRefsOf(id);
  const patchField = useCodexStore((s) => s.patchField);
  const patchEntity = useCodexStore((s) => s.patchEntity);
  const removeCustomField = useCodexStore((s) => s.removeCustomField);
  const deleteEntity = useCodexStore((s) => s.deleteEntity);
  const [, setFocus] = useFocus();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addingField, setAddingField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  if (!entity) {
    return (
      <Aside>
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-faint">엔티티를 찾을 수 없습니다.</p>
          <Button variant="ghost" size="sm" onClick={() => setFocus(null)}>
            닫기
          </Button>
        </div>
      </Aside>
    );
  }

  const M = ENTITY_META[entity.type];
  const Icon = M.icon;
  const customKeys = new Set((entity.customFields ?? []).map((f) => f.key));
  const fields = fieldsFor(entity.type, entity.customFields);
  const groups: { name: string; fields: FieldDef[] }[] = [];
  for (const f of fields) {
    const g = f.group ?? '필드';
    let grp = groups.find((x) => x.name === g);
    if (!grp) {
      grp = { name: g, fields: [] };
      groups.push(grp);
    }
    grp.fields.push(f);
  }

  const addCustomField = () => {
    const label = newFieldLabel.trim();
    if (!label) return;
    const next: FieldDef = { key: `cf_${newId()}`, label, type: 'longtext', group: '커스텀', order: 500 };
    patchEntity(id, { customFields: [...(entity.customFields ?? []), next] });
    setNewFieldLabel('');
    setAddingField(false);
  };

  return (
    <Aside>
      {/* 헤더 */}
      <div className="sticky top-0 z-10 border-b border-line bg-bg-2/80 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
            <Icon className="size-3.5" style={{ color: entityColor(entity) }} />
            {M.label}
            {entitySummary(entity) && <span className="text-fainter">· {entitySummary(entity)}</span>}
          </div>
          <button
            type="button"
            onClick={() => setFocus(null)}
            className="text-faint transition hover:text-ink"
            aria-label="인스펙터 닫기"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="mt-2">
          <EditableText
            value={entity.name}
            onCommit={(v) => patchEntity(id, { name: v || '제목 없음' })}
            className="font-serif !text-xl font-bold text-white"
          />
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <CanonBadge canonStatus={entity.canonStatus} confidence={entity.confidence} showConfidence />
          <button
            type="button"
            onClick={() => navigate('/')}
            title="커널에게 검수 지시(홈)"
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-star/40 bg-star/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-star transition hover:bg-star/20"
          >
            <Wand2 className="size-3" /> 커널
          </button>
        </div>
      </div>

      <div className="px-4 pb-24">
        {/* 필드 그룹 */}
        {groups.map((g) => (
          <div key={g.name}>
            <SectionLabel>{g.name}</SectionLabel>
            <div className="space-y-3">
              {g.fields.map((f) => (
                <div key={f.key} className="group/cf relative">
                  <FieldRow
                    field={f}
                    value={entity.fields[f.key]}
                    onChange={(v) => patchField(id, f.key, v)}
                  />
                  {customKeys.has(f.key) && (
                    <button
                      type="button"
                      onClick={() => removeCustomField(id, f.key)}
                      className="absolute right-0 top-0 text-fainter opacity-0 transition hover:text-danger group-hover/cf:opacity-100"
                      title="커스텀 필드 삭제"
                      aria-label="커스텀 필드 삭제"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 커스텀 필드 추가 */}
        {addingField ? (
          <div className="mt-3 flex items-center gap-2">
            <input
              autoFocus
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomField()}
              placeholder="필드 이름…"
              className="h-7 flex-1 rounded-sm border border-line-2 bg-panel px-2 text-xs text-ink focus:border-star focus:outline-none"
            />
            <Button size="sm" variant="outline" onClick={addCustomField}>
              추가
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingField(true)}
            className="mt-3 inline-flex items-center gap-1 text-xs text-faint transition hover:text-star"
          >
            <Plus className="size-3" /> 커스텀 필드
          </button>
        )}

        {/* 관계 */}
        <SectionLabel>관계</SectionLabel>
        <RelationEditor entityId={id} />

        {/* 등장 (역참조) */}
        <SectionLabel>등장 · 역참조</SectionLabel>
        {refs.length === 0 ? (
          <p className="text-xs text-fainter">아직 이 엔티티를 언급하는 노트가 없다.</p>
        ) : (
          <div className="space-y-1">
            {refs.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setFocus(r.id)}
                className="block w-full truncate text-left text-sm text-dim transition hover:text-star"
              >
                · {r.name}
              </button>
            ))}
          </div>
        )}

        {/* 태그 */}
        <SectionLabel>태그</SectionLabel>
        <TagInput tags={entity.tags ?? []} onChange={(t) => patchEntity(id, { tags: t })} />

        {/* 캐논 상태 */}
        <SectionLabel>캐논 상태</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <Select
            value={entity.canonStatus}
            onValueChange={(v) => patchEntity(id, { canonStatus: v as never })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CANON_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={entity.confidence}
            onValueChange={(v) => patchEntity(id, { confidence: v as never })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 삭제 */}
        <SectionLabel>위험</SectionLabel>
        <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
          <Trash2 className="size-4" /> 삭제 (휴지통)
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`"${entity.name}" 삭제`}
        description="휴지통으로 이동합니다 (복원 가능). 연결된 관계도 함께 숨겨집니다."
        confirmLabel="삭제"
        onConfirm={() => {
          deleteEntity(id);
          setFocus(null);
        }}
      />
    </Aside>
  );
}

function Aside({ children }: { children: ReactNode }) {
  return (
    <aside className="sticky top-[61px] h-[calc(100vh-61px)] w-[384px] flex-none animate-in slide-in-from-right overflow-y-auto border-l border-line bg-bg-2/40">
      {children}
    </aside>
  );
}
